import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, RefreshCw, ArrowLeft, ExternalLink, Filter,
  Banknote, HandCoins, Receipt, Building2, TrendingUp, Rocket,
  Handshake, Gavel, Briefcase, Plane, GraduationCap, Home, Landmark,
  LineChart, HeartHandshake, Target as TargetIcon, ChevronRight,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

const CATEGORY_ICONS = {
  financing: Banknote, grants: HandCoins, tax_credits: Receipt, gov_programs: Building2,
  investors: TrendingUp, incubators: Rocket, partners: Handshake, tenders: Gavel,
  career: Briefcase, immigration: Plane, training: GraduationCap, real_estate: Home,
  fiscal: Landmark, investments: LineChart, private_aid: HeartHandshake, business: TargetIcon,
};

const PROB_TONE = {
  very_high: "bg-emerald-100 text-emerald-900 border-emerald-300",
  high:      "bg-lime-100 text-lime-900 border-lime-300",
  medium:    "bg-amber-100 text-amber-900 border-amber-300",
  low:       "bg-red-100 text-red-900 border-red-300",
};
const PROB_LABEL = { very_high: "Très élevée", high: "Élevée", medium: "Modérée", low: "Faible" };

function formatCad(min, max, currency = "CAD") {
  const fmt = (v) => v == null ? "" : new Intl.NumberFormat("fr-CA", { style: "currency", currency, maximumFractionDigits: 0 }).format(v);
  if (!min && !max) return "—";
  if (min === max) return fmt(min);
  return `${fmt(min)} – ${fmt(max)}`;
}


export default function DossierOpportunities() {
  const { dossier_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadLatest = async () => {
    setLoading(true);
    try {
      const [{ data: latest }, { data: cats }] = await Promise.all([
        api.get(`/global-opportunities/for-dossier/${dossier_id}/latest`),
        api.get(`/global-opportunities/categories`),
      ]);
      setData(latest.empty ? null : latest);
      setCategories(cats.categories || []);
    } catch (err) {
      toast.error("Impossible de charger les opportunités.");
    } finally { setLoading(false); }
  };

  const runMatch = async (force = false) => {
    setRunning(true);
    try {
      const { data: m } = await api.post(`/global-opportunities/for-dossier/${dossier_id}/match?top_k=20${force ? "&force=true" : ""}`);
      setData(m);
      if (m.cached) toast.info("Cache — mêmes conditions dossier, résultats inchangés.");
      else toast.success(`${m.count_ranked || 0} opportunités classées.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Matching impossible.");
    } finally { setRunning(false); }
  };

  useEffect(() => { loadLatest(); /* eslint-disable-next-line */ }, [dossier_id]);

  const grouped = data?.grouped || {};
  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (!selectedCat) return data.items;
    return data.items.filter((o) => o.category === selectedCat);
  }, [data, selectedCat]);

  const categoryCounts = useMemo(() => {
    const m = {};
    (data?.items || []).forEach((o) => { m[o.category] = (m[o.category] || 0) + 1; });
    return m;
  }, [data]);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="dossier-opportunities-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to={`/app/dossiers/${dossier_id}`}
                  data-testid="opps-back-to-dossier"
                  className="text-xs text-[#5B5B62] hover:text-[#0B2545] inline-flex items-center gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour au dossier
            </Link>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14] mt-3">
              Moteur global d'opportunités
            </div>
            <h1 className="mt-1 font-display text-3xl tracking-tight text-[#1F2937]">
              Opportunités détectées pour ce dossier
            </h1>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-2xl">
              Financement, subventions, crédits d'impôt, immigration, incubateurs, formations, partenaires…
              ALTIORA détecte ce qui pourrait améliorer la trajectoire économique de ce client.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => runMatch(false)} disabled={running}
                    data-testid="opps-match-button"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2 text-sm font-medium transition-colors">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {running ? "Matching IA…" : "Lancer le matching"}
            </button>
            {data && (
              <button onClick={() => runMatch(true)} disabled={running}
                      data-testid="opps-force-match"
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] disabled:opacity-60 text-[#1F2937] px-3 py-2 text-xs font-medium transition-colors">
                <RefreshCw className="h-3 w-3" /> Recalculer
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="p-12 flex items-center justify-center text-[#5B5B62]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        )}

        {!loading && !data && (
          <div className="rounded-2xl border-2 border-dashed border-[#C5A880]/60 bg-white/70 p-10 text-center"
               data-testid="opps-empty">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#FBF6EA] flex items-center justify-center">
              <Sparkles className="h-7 w-7 text-[#7A5B14]" strokeWidth={1.5} />
            </div>
            <h3 className="mt-5 font-display text-2xl text-[#1F2937] tracking-tight">
              Aucune analyse d'opportunités lancée
            </h3>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-lg mx-auto">
              Cliquez sur « Lancer le matching » — ALTIORA parcourt son registre de {" "}
              <strong>20 opportunités curées</strong>, filtre celles compatibles avec ce dossier
              (pays, secteur, type client), et Claude Sonnet 4.5 les classe par pertinence.
            </p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Stats bar */}
            <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-[#5B5B62]" data-testid="opps-stats">
              <span><strong className="text-[#0B2545]">{data.count_ranked || 0}</strong> opportunités classées</span>
              <span>·</span>
              <span>{data.count_candidates || 0} candidats après filtre</span>
              <span>·</span>
              <span>Contexte : {data.context?.country} · {data.context?.client_type}</span>
              {data.context?.sector && <span>· {data.context.sector}</span>}
              {data.cached && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FBF6EA] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                  <CheckCircle2 className="h-2.5 w-2.5" /> Cache
                </span>
              )}
            </div>

            {/* Category filter */}
            <div className="mb-6 flex flex-wrap gap-2" data-testid="opps-category-filter">
              <button onClick={() => setSelectedCat(null)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors
                        ${!selectedCat ? "bg-[#0B2545] text-white" : "bg-white border border-[#E4E1D8] text-[#1F2937] hover:border-[#0B2545]"}`}>
                Toutes ({data.count_ranked || 0})
              </button>
              {categories.filter((c) => categoryCounts[c.key] > 0).map((c) => {
                const Icon = CATEGORY_ICONS[c.key] || TargetIcon;
                const active = selectedCat === c.key;
                return (
                  <button key={c.key} onClick={() => setSelectedCat(c.key === selectedCat ? null : c.key)}
                          data-testid={`opps-cat-${c.key}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors
                            ${active ? "bg-[#0B2545] text-white" : "bg-white border border-[#E4E1D8] text-[#1F2937] hover:border-[#0B2545]"}`}>
                    <Icon className="h-3 w-3" />
                    {c.label_fr} ({categoryCounts[c.key]})
                  </button>
                );
              })}
            </div>

            {/* Opportunity grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredItems.map((o) => <OpportunityCard key={o.opportunity_id} opp={o} onOpen={() => setDetail(o)} />)}
            </div>

            {filteredItems.length === 0 && (
              <div className="rounded-lg border border-dashed border-[#E4E1D8] bg-white p-10 text-center text-sm text-[#5B5B62]">
                Aucune opportunité dans cette catégorie pour ce dossier.
              </div>
            )}
          </>
        )}

        {detail && <OpportunityDrawer opp={detail} onClose={() => setDetail(null)} dossierId={dossier_id} />}
      </div>
    </div>
  );
}


function OpportunityCard({ opp, onOpen }) {
  const Icon = CATEGORY_ICONS[opp.category] || TargetIcon;
  const rank = opp.ai_rank || {};
  const probTone = PROB_TONE[rank.probability_level] || PROB_TONE.medium;
  return (
    <button onClick={onOpen}
            data-testid={`opp-card-${opp.opportunity_id}`}
            className="text-left border-2 border-[#E5E7EB] bg-white rounded-lg p-4 hover:border-[#0B2545] hover:shadow-md transition-all group">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#FBF6EA] flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.8} />
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#7A5B14]">
            {opp.category?.replace("_", " ")}
          </div>
        </div>
        {rank.relevance_score != null && (
          <div className="font-mono text-sm text-[#0B2545] tabular-nums">
            {rank.relevance_score}<span className="text-[10px] opacity-60">/100</span>
          </div>
        )}
      </div>
      <h3 className="font-display text-base text-[#0B2545] leading-tight mb-1 line-clamp-2">{opp.name_fr}</h3>
      <div className="text-[11px] text-[#5B5B62] mb-2">{opp.provider_fr}</div>
      {rank.why_relevant_fr && (
        <p className="text-xs text-[#1F2937] leading-relaxed line-clamp-3 mb-3">{rank.why_relevant_fr}</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-[#E4E1D8]">
        {rank.probability_level && (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${probTone}`}>
            {PROB_LABEL[rank.probability_level]}
          </span>
        )}
        {opp.benefit?.amount_max > 0 && (
          <span className="text-[10px] font-mono text-[#5B5B62]">
            {formatCad(opp.benefit.amount_min, opp.benefit.amount_max, opp.benefit.currency)}
          </span>
        )}
      </div>
    </button>
  );
}


function OpportunityDrawer({ opp, onClose, dossierId }) {
  const rank = opp.ai_rank || {};
  const probTone = PROB_TONE[rank.probability_level] || PROB_TONE.medium;
  const Icon = CATEGORY_ICONS[opp.category] || TargetIcon;

  const track = async (action) => {
    try {
      await api.post(`/global-opportunities/for-dossier/${dossierId}/action`, {
        opportunity_id: opp.opportunity_id, action,
      });
      toast.success(action === "interested" ? "Ajoutée au plan." : action === "applied" ? "Suivi enregistré." : "Notée.");
    } catch { toast.error("Action impossible."); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={onClose} data-testid="opp-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-[#7A5B14]" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
              {opp.category?.replace("_", " ")}
            </div>
          </div>
          <button onClick={onClose} className="text-[#5B5B62] hover:text-[#0B2545] text-xl">×</button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <h2 className="font-display text-2xl text-[#0B2545] leading-tight">{opp.name_fr}</h2>
            <div className="text-sm text-[#5B5B62] mt-1">{opp.provider_fr}</div>
          </div>

          {rank.relevance_score != null && (
            <div className="rounded-lg border-2 border-[#0B2545]/25 bg-[#FBF6EA]/40 p-4 space-y-2">
              <div className="flex items-center gap-3">
                <div className="font-display text-3xl text-[#0B2545] tabular-nums">{rank.relevance_score}<span className="text-sm opacity-60">/100</span></div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Pertinence IA</div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest mt-0.5 ${probTone}`}>
                    Probabilité {PROB_LABEL[rank.probability_level]}
                  </span>
                </div>
              </div>
              {rank.why_relevant_fr && (
                <div className="pt-2 border-t border-[#0B2545]/15">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">Pourquoi cette opportunité</div>
                  <p className="text-sm text-[#1F2937] leading-relaxed">{rank.why_relevant_fr}</p>
                </div>
              )}
              {rank.estimated_impact_fr && (
                <div className="pt-2 border-t border-[#0B2545]/15">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">Impact estimé</div>
                  <p className="text-sm text-[#1F2937] leading-relaxed">{rank.estimated_impact_fr}</p>
                </div>
              )}
              {rank.priority_action_fr && (
                <div className="pt-2 border-t border-[#0B2545]/15">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-1">Prochaine action</div>
                  <p className="text-sm text-[#0B2545] font-medium leading-relaxed">{rank.priority_action_fr}</p>
                </div>
              )}
              {rank.risk_flags_fr?.length > 0 && (
                <div className="pt-2 border-t border-[#0B2545]/15">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-red-800 mb-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Points de vigilance
                  </div>
                  <ul className="text-xs text-red-900 space-y-0.5">
                    {rank.risk_flags_fr.map((r, i) => <li key={i}>• {r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div>
            <h4 className="font-display text-sm text-[#0B2545] mb-2">Description</h4>
            <p className="text-sm text-[#1F2937] leading-relaxed">{opp.detail_fr || opp.summary_fr}</p>
          </div>

          {opp.benefit && (
            <div className="grid grid-cols-2 gap-3">
              <StatBox label="Type" value={opp.benefit.type} />
              <StatBox label="Montant" value={formatCad(opp.benefit.amount_min, opp.benefit.amount_max, opp.benefit.currency)} />
              <StatBox label="Difficulté" value={opp.process?.difficulty || "—"} />
              <StatBox label="Délai typique" value={opp.process?.typical_delay_weeks ? `${opp.process.typical_delay_weeks} semaines` : "—"} />
            </div>
          )}

          {opp.process?.steps_fr?.length > 0 && (
            <div>
              <h4 className="font-display text-sm text-[#0B2545] mb-2">Étapes</h4>
              <ol className="space-y-1.5 text-sm text-[#1F2937]">
                {opp.process.steps_fr.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="font-mono text-[10px] text-[#7A5B14] mt-1">{i + 1}.</span><span>{s}</span></li>
                ))}
              </ol>
            </div>
          )}

          {opp.process?.required_docs_fr?.length > 0 && (
            <div>
              <h4 className="font-display text-sm text-[#0B2545] mb-2">Documents requis</h4>
              <ul className="space-y-1 text-sm text-[#1F2937]">
                {opp.process.required_docs_fr.map((d, i) => (
                  <li key={i} className="flex gap-2"><ChevronRight className="h-3 w-3 opacity-40 mt-1" /><span>{d}</span></li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E4E1D8]">
            <button onClick={() => track("interested")}
                    data-testid="opp-action-interested"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm font-medium">
              Ajouter au plan
            </button>
            <button onClick={() => track("applied")}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] px-4 py-2 text-sm">
              Marquer « demande soumise »
            </button>
            <button onClick={() => track("dismissed")}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-red-400 hover:text-red-800 px-4 py-2 text-sm">
              Non pertinente
            </button>
            {opp.source_url && (
              <a href={opp.source_url} target="_blank" rel="noreferrer"
                 className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#7A5B14] hover:underline">
                Source officielle <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function StatBox({ label, value }) {
  return (
    <div className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3]/50 p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <div className="font-display text-sm text-[#0B2545] mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
